/**
 * Deep clone utility that creates a complete copy of an object/array breaking all references
 * Handles primitive values, objects, arrays, dates, and other common types
 *
 * @param obj - The object to clone
 * @returns A deep clone of the input object with no shared references
 */
export function deepClone<T>(obj: T): T {
    // Handle null and undefined
    if (obj === null || obj === undefined) {
        return obj;
    }

    // Handle primitive types (string, number, boolean, symbol, bigint)
    if (typeof obj !== "object") {
        return obj;
    }

    // Handle Date objects
    if (obj instanceof Date) {
        return new Date(obj.getTime()) as T;
    }

    // Handle RegExp objects
    if (obj instanceof RegExp) {
        return new RegExp(obj.source, obj.flags) as T;
    }

    // Handle Arrays
    if (Array.isArray(obj)) {
        return obj.map(item => deepClone(item)) as T;
    }

    // Handle plain objects
    if (obj.constructor === Object || obj.constructor === undefined) {
        const cloned = {} as T;
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                (cloned as any)[key] = deepClone((obj as any)[key]);
            }
        }
        return cloned;
    }

    // Handle other object types by attempting to clone their enumerable properties
    // This covers most cases but may not work for complex objects with internal state
    try {
        const cloned = Object.create(Object.getPrototypeOf(obj));
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                (cloned as any)[key] = deepClone((obj as any)[key]);
            }
        }
        return cloned;
    } catch (error) {
        console.warn("Deep clone failed for object:", obj, "Error:", error);
        return obj; // Return original if cloning fails
    }
}

/**
 * Alternative deep clone using structuredClone (modern browsers only)
 * Falls back to custom deepClone if structuredClone is not available
 *
 * @param obj - The object to clone
 * @returns A deep clone of the input object
 */
export function deepCloneFast<T>(obj: T): T {
    // Use native structuredClone if available (faster and more reliable)
    if (typeof structuredClone !== "undefined") {
        try {
            return structuredClone(obj);
        } catch (error) {
            // structuredClone can fail on some objects (functions, symbols, etc.)
            console.warn("structuredClone failed, falling back to custom implementation:", error);
        }
    }

    // Fallback to custom implementation
    return deepClone(obj);
}

/**
 * Simple deep clone using JSON methods
 * Fast but limited - does not handle functions, dates properly, undefined values, etc.
 * Use only for simple objects with JSON-serializable data
 *
 * @param obj - The object to clone (must be JSON serializable)
 * @returns A deep clone of the input object
 */
export function deepCloneJSON<T>(obj: T): T {
    try {
        return JSON.parse(JSON.stringify(obj));
    } catch (error) {
        console.error("JSON deep clone failed:", error);
        throw new Error("Object is not JSON serializable");
    }
}

/**
 * Alias for deepClone - more descriptive name for the main use case
 * Duplicates an object while breaking all references
 *
 * @param obj - The object to duplicate
 * @returns A duplicate of the input object with no shared references
 */
export function duplicateObject<T>(obj: T): T {
    return deepClone(obj);
}
